import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, PlusLg, DashLg } from 'react-bootstrap-icons'
import { useData } from '../store.jsx'
import { PageHeader, Panel, Stat, FormModal, RowActions, Amount } from '../components/ui.jsx'
import { BarList } from '../components/charts.jsx'
import { entries, categoryTotals } from '../lib/finance.js'
import { money, monthLabel, dateLabel, todayISO } from '../lib/format.js'
import { INCOME_CATEGORIES, EXPENSE_CATEGORIES } from '../lib/constants.js'
import { add, setAt, removeAt } from '../lib/firebase.js'

export function transactionFields(accounts) {
  return [
    { name: 'type', label: 'Type', type: 'select', required: true, col: 'col-6', options: [{ id: 'income', label: 'Income' }, { id: 'expense', label: 'Outcome' }] },
    { name: 'date', label: 'Date', type: 'date', required: true, col: 'col-6' },
    { name: 'amount', label: 'Amount', type: 'number', required: true, col: 'col-6', step: '0.01' },
    { name: 'category', label: 'Category', type: 'select', required: true, col: 'col-6', options: (v) => (v.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES) },
    { name: 'accountId', label: 'Account', type: 'select', options: accounts.map((a) => ({ id: a.id, label: a.name })) },
    { name: 'note', label: 'Note', type: 'text' },
  ]
}

const shiftMonth = (m, n) => { const [y, mo] = m.split('-').map(Number); const d = new Date(y, mo - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` }

export default function Transactions() {
  const data = useData()
  const [month, setMonth] = useState(todayISO().slice(0, 7))
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [edit, setEdit] = useState(null)
  const accounts = entries(data.accounts)
  const accountName = Object.fromEntries(accounts.map((a) => [a.id, a.name]))

  const list = useMemo(() => entries(data.transactions)
    .filter((t) => (month === 'all' || t.date.startsWith(month)) && (filter === 'all' || t.type === filter))
    .filter((t) => !search || `${t.note} ${t.category}`.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => b.date.localeCompare(a.date) || (b.createdAt || 0) - (a.createdAt || 0)), [data.transactions, month, filter, search])

  const income = list.filter((t) => t.type === 'income').reduce((s, t) => s + t.amount, 0)
  const expense = list.filter((t) => t.type === 'expense').reduce((s, t) => s + t.amount, 0)
  const cats = categoryTotals(data.transactions, month === 'all' ? null : month, 'expense').map((c) => ({ label: c.category, value: c.amount }))

  async function save(values) {
    const { id, ...rest } = edit
    const record = { ...rest, ...values, amount: Math.abs(values.amount) }
    if (id) await setAt(`transactions/${id}`, record)
    else await add('transactions', { ...record, createdAt: Date.now() })
  }

  return (
    <>
      <PageHeader title="Income & outcome" subtitle={month === 'all' ? 'All time' : monthLabel(month)}>
        <button className="btn btn-outline-success" onClick={() => setEdit({ type: 'income', date: todayISO(), category: 'Salary' })}><PlusLg /> Income</button>
        <button className="btn btn-outline-danger" onClick={() => setEdit({ type: 'expense', date: todayISO(), category: 'Other' })}><DashLg /> Outcome</button>
      </PageHeader>

      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        <div className="btn-group">
          <button className="btn btn-outline-secondary" onClick={() => setMonth(shiftMonth(month === 'all' ? todayISO().slice(0, 7) : month, -1))}><ChevronLeft /></button>
          <input type="month" className="form-control rounded-0" style={{ width: 170 }} value={month === 'all' ? '' : month} onChange={(e) => setMonth(e.target.value || 'all')} />
          <button className="btn btn-outline-secondary" onClick={() => setMonth(shiftMonth(month === 'all' ? todayISO().slice(0, 7) : month, 1))}><ChevronRight /></button>
        </div>
        <button className={`btn ${month === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setMonth('all')}>All time</button>
        <select className="form-select w-auto" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">Income & outcome</option><option value="income">Income only</option><option value="expense">Outcome only</option>
        </select>
        <input className="form-control w-auto" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="row g-3 mb-3">
        <div className="col-4"><Stat label="Income" value={money(income)} tone="pos" /></div>
        <div className="col-4"><Stat label="Outcome" value={money(expense)} tone="neg" /></div>
        <div className="col-4"><Stat label="Balance" value={money(income - expense, { sign: true })} tone={income - expense < 0 ? 'neg' : 'pos'}
          hint={income ? `Savings rate ${Math.round(((income - expense) / income) * 100)}%` : null} /></div>
      </div>

      <div className="row g-3">
        <div className="col-lg-8">
          <Panel title={`${list.length} transactions`}>
            {list.length ? (
              <div className="table-wrap"><table className="table table-hover align-middle">
                <thead><tr><th>Date</th><th>Category</th><th>Note</th><th>Account</th><th className="text-end">Amount</th><th /></tr></thead>
                <tbody>
                  {list.map((t) => (
                    <tr key={t.id}>
                      <td className="text-nowrap small">{dateLabel(t.date)}</td>
                      <td><span className={`badge ${t.type === 'income' ? 'text-bg-success' : 'text-bg-secondary'} bg-opacity-75`}>{t.category}</span></td>
                      <td className="small">{t.note}{t.recurringId && <span className="text-body-secondary"> · recurring</span>}</td>
                      <td className="small text-body-secondary">{accountName[t.accountId] || ''}</td>
                      <td className="num"><Amount value={t.amount} type={t.type} /></td>
                      <td className="text-end"><RowActions onEdit={() => setEdit(t)} onDelete={() => removeAt(`transactions/${t.id}`)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            ) : <div className="empty">No transactions for this period.</div>}
          </Panel>
        </div>
        <div className="col-lg-4">
          <Panel title="Outcome by category"><BarList items={cats} total={expense} /></Panel>
        </div>
      </div>

      {edit && <FormModal title={edit.id ? 'Edit transaction' : edit.type === 'income' ? 'New income' : 'New outcome'}
        fields={transactionFields(accounts)} initial={edit} onSubmit={save} onClose={() => setEdit(null)} />}
    </>
  )
}
